import React from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import useRegisterForm from "../../lib/Register/useRegisterForm";
import {
  TITLE_TH_LIST,
  onlyThai,
  onlyEng,
  onlyEngNoSpace,
  onlyEngTitle,
  onlyNumber,
  onlyAlnum,
  onlyEmail,
} from "../../lib/Register/registerValidators";
import RegisterShell from "./RegisterShell";
import { RegisterField, RegisterSelect } from "./RegisterFields";

const rowStyle = { padding: "5px 10px" };

// หัวข้อกลุ่มในฟอร์ม
const SectionTitle = ({ children }) => (
  <div
    style={{
      margin: "26px 10px 14px",
      paddingBottom: "8px",
      fontSize: "17px",
      fontWeight: 700,
      color: "#053075",
      borderBottom: "1px solid rgba(5, 48, 117, 0.35)",
    }}
  >
    {children}
  </div>
);

// หน้าลงทะเบียนปกติ: /:prefix/register
// (business ที่มี service employee-pre-register-pmuc ใช้หน้า RegisterPmuc แทน)
function Register({ prefix, services = [] }) {
  const navigate = useNavigate();
  const form = useRegisterForm({ prefix });
  const { bind, caRequest } = form;

  // ติ๊กขอ CA ได้เฉพาะ business ที่มี service ca + ca-pre-register
  const hasCaService = services.includes("ca") && services.includes("ca-pre-register");

  return (
    <RegisterShell
      onCancel={() => navigate(`/${prefix}`)}
      onSubmit={() => form.handleSubmit()}
      submitDisabled={form.loading || !form.isFormValid}
      loading={form.loading}
      snackbarOpen={form.snackbarOpen}
      onCloseSnackbar={form.closeSnackbar}
    >
      {/* ---------- ข้อมูลผู้ลงทะเบียน ---------- */}
      <SectionTitle>ข้อมูลผู้ลงทะเบียน</SectionTitle>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("employeeId")}
          onValidate={undefined}
          lg={8}
          label="รหัสพนักงาน (ถ้ามี)"
          placeholder="รหัสพนักงาน"
          maxLength={20}
        />
        <RegisterSelect
          {...bind("titleTh")}
          lg={4}
          label="คำนำหน้า"
          placeholder="คำนำหน้า"
          options={TITLE_TH_LIST}
        />
      </Row>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("specialTitleTh", onlyThai)}
          onValidate={undefined}
          label="คำนำหน้าชื่อพิเศษ (ภาษาไทย) (ถ้ามี)"
          placeholder="คำนำหน้าชื่อพิเศษ (ภาษาไทย)"
          maxLength={50}
        />
        <RegisterField
          {...bind("specialTitleEng", onlyEngTitle)}
          onValidate={undefined}
          label="คำนำหน้าชื่อพิเศษ (ภาษาอังกฤษ) (ถ้ามี)"
          placeholder="คำนำหน้าชื่อพิเศษ (ภาษาอังกฤษ)"
          maxLength={50}
        />
      </Row>

      <Row style={rowStyle}>
        <RegisterField {...bind("nameTh", onlyThai)} label="ชื่อ (ภาษาไทย)" maxLength={50} />
        <RegisterField
          {...bind("surnameTh", onlyThai)}
          label="นามสกุล (ภาษาไทย)"
          maxLength={50}
        />
      </Row>

      <Row style={rowStyle}>
        <RegisterField {...bind("nameEng", onlyEng)} label="ชื่อ (ภาษาอังกฤษ)" maxLength={50} />
        <RegisterField
          {...bind("surnameEng", onlyEngNoSpace)}
          label="นามสกุล (ภาษาอังกฤษ)"
          maxLength={50}
        />
      </Row>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("cid", onlyNumber)}
          lg={12}
          label={`เลขประจำตัวประชาชน (Identification Card)${
            caRequest ? "" : " (ถ้ามี)"
          } *ไม่ต้องมีขีดกลาง (-) หรือเว้นวรรค`}
          placeholder={caRequest ? "เลขประจำตัวประชาชน" : "เลขประจำตัวประชาชน (ถ้ามี)"}
          maxLength={20}
        />
      </Row>

      {/* ---------- ข้อมูลติดต่อ ---------- */}
      <SectionTitle>ข้อมูลติดต่อ</SectionTitle>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("tel", onlyNumber)}
          lg={4}
          label="โทรศัพท์ (ถ้ามี) *ไม่ต้องมีขีดกลาง (-) หรือเว้นวรรค"
          placeholder="โทรศัพท์ (ถ้ามี)"
          maxLength={10}
        />
        <RegisterField
          {...bind("mobile", onlyNumber)}
          lg={4}
          label="โทรศัพท์มือถือ *ไม่ต้องมีขีดกลาง (-) หรือเว้นวรรค"
          placeholder="โทรศัพท์มือถือ"
          maxLength={10}
        />
        <RegisterField {...bind("email", onlyEmail)} lg={4} label="อีเมล" />
      </Row>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("username", onlyAlnum)}
          label="Username (ถ้ามี)"
          placeholder="Username (ถ้ามี)"
        />
      </Row>

      {/* ---------- ข้อมูลหน่วยงาน ---------- */}
      <SectionTitle>ข้อมูลหน่วยงาน</SectionTitle>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("position")}
          onValidate={undefined}
          label="ตำแหน่ง (T) (ถ้ามี) (ภาษาไทย หรือ ภาษาอังกฤษ)"
          placeholder="ตำแหน่ง (ถ้ามี)"
        />
        <RegisterField
          {...bind("department")}
          onValidate={undefined}
          label="แผนก (T) (ถ้ามี) (ภาษาไทย หรือ ภาษาอังกฤษ)"
          placeholder="แผนก (ถ้ามี)"
        />
      </Row>

      <Row style={rowStyle}>
        <RegisterField
          {...bind("bizEmail", onlyEmail)}
          label={`อีเมลองค์กร${caRequest ? "" : " (ถ้ามี)"}`}
          placeholder="อีเมลองค์กร"
        />
      </Row>

      {/* ---------- ใบรับรองอิเล็กทรอนิกส์ (CA) ---------- */}
      {hasCaService && (
        <>
          <SectionTitle>ใบรับรองอิเล็กทรอนิกส์ (CA)</SectionTitle>

          <Row style={rowStyle}>
            <Col lg={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={caRequest}
                    onChange={(e) => form.toggleCa(e.target.checked)}
                    sx={{
                      color: "#053075",
                      "&.Mui-checked": { color: "#053075" },
                    }}
                  />
                }
                label={
                  <span style={{ color: "#000", fontWeight: 500 }}>
                    ขอใบรับรองอิเล็กทรอนิกส์ (CA)
                  </span>
                }
              />
            </Col>
          </Row>

          {caRequest && (
            <Row style={rowStyle}>
              <RegisterField
                {...bind("subOrganization")}
                label="ชื่อของหน่วยงานย่อยในองค์กรที่เจ้าของใบรับรองสังกัด"
                maxLength={200}
              />
              <RegisterField {...bind("address")} label="ที่อยู่" maxLength={300} />
            </Row>
          )}
        </>
      )}
    </RegisterShell>
  );
}

export default Register;